import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Paper,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  WbSunny,
  WbTwilight,
  Nightlight,
  Flare,
  AccessAlarm,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import moment from 'moment';
import TimeGridRow from '../../TimeGridRow/TimeGridRow';

interface DefaultAvailabilitySubmissionModalProps {
  open: boolean;
  currentDate: string;
  defaultAvailabilities: Record<string, any>[],
  onClose: () => void;
  onSubmit: (newAvailability: Record<string, any>, currentDate: string) => Promise<void>;
}

const DefaultAvailabilitySubmissionModal: React.FC<DefaultAvailabilitySubmissionModalProps> = ({
  open,
  currentDate,
  defaultAvailabilities,
  onClose,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(moment().format("ddd"));
  const [availability, setAvailability] = useState<Record<string, any>>({});
  const [newAvailability, setNewAvailability] = useState<Record<string, any>>({});
  const [mergedAvailability, setMergedAvailability] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDay, setPendingDay] = useState<string | null>(null);
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(newAvailability, selectedDay);
      onClose();
    } catch (error) {
      console.error('Error submitting availability:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setNewAvailability(JSON.parse(JSON.stringify(availability)));
      setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(availability).filter((v: string) => v !== '')))));
      onClose();
    }
  };

  const mergeAvailability = (availability: string[]): string[] => {
    const sortedRanges = availability
      .map((range) => {
        const [start, end] = range.split('-');
        return { start: moment(start, 'HH:mm'), end: moment(end, 'HH:mm') };
      })
      .sort((a, b) => a.start.diff(b.start));

    const mergedRanges: { start: moment.Moment; end: moment.Moment }[] = [];
    for (const range of sortedRanges) {
      if (
        mergedRanges.length > 0 &&
        mergedRanges[mergedRanges.length - 1].end.isSameOrAfter(range.start)
      ) {
        mergedRanges[mergedRanges.length - 1].end = moment.max(
          mergedRanges[mergedRanges.length - 1].end,
          range.end
        );
      } else {
        mergedRanges.push(range);
      }
    }

    return mergedRanges.map(
      (range) =>
        `${range.start.format('HH:mm')}-${range.end.format('HH:mm')}`
    );
  };

  const handlePreselectAvailability = (preselectType: string) => {
    const updatedAvailability: Record<string, any> = {};
    for (let hour = 0; hour < 24; hour++) {
      let newRange: string = hour < 10 ? `0${hour}:00-${hour + 1 < 10 ? `0${hour + 1}` : hour + 1}:00` : `${hour}:00-${hour + 1}:00`;
      switch (preselectType) {
        case "9/5":
          if (hour >= 9 && hour < 17) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "morning":
          if (hour >= 6 && hour < 12) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "afternoon":
          if (hour >= 12 && hour < 17) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "evening":
          if (hour >= 17 && hour < 21) {
            updatedAvailability[hour] = newRange;
          }
          break;
        case "night":
          if (hour >= 21 || hour < 6) {
            updatedAvailability[hour] = newRange;
          }
          break;
      }
    }
    setNewAvailability(updatedAvailability);
    setMergedAvailability(mergeAvailability(Object.values(updatedAvailability).filter((v: string) => v !== '')));
  };

  React.useEffect(() => {
    const availability: Record<string, any> = defaultAvailabilities?.find((v: Record<string, any>) => v.dayUserId.split("#")[1] === (selectedDay))?.availability || {}
    setAvailability(availability)
    setNewAvailability(JSON.parse(JSON.stringify(availability)));
    setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(availability).filter((v: string) => v !== '')))));
  }, [defaultAvailabilities, selectedDay])

  React.useEffect(() => {
    setNewAvailability(JSON.parse(JSON.stringify(availability)));
    setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(availability).filter((v: string) => v !== '')))));
  }, [availability])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <CalendarIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Update Your Default Availability
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(selectedDay, 'ddd').format("dddd")}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Availability Preselects Section */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'text.primary' }}>
              Quick Presets
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button 
                variant='contained' 
                color="primary" 
                onClick={(() => handlePreselectAvailability("9/5"))}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                <AccessAlarm sx={{ mr: 1, fontSize: 20 }} />
                9/5
              </Button>
              <Button 
                variant='contained' 
                color="primary"
                onClick={(() => handlePreselectAvailability("morning"))}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                <Flare sx={{ mr: 1, fontSize: 20 }} />
                Morning
              </Button>
              <Button 
                variant='contained' 
                color="primary"
                onClick={(() => handlePreselectAvailability("afternoon"))}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                <WbSunny sx={{ mr: 1, fontSize: 20 }} />
                Afternoon
              </Button>
              <Button 
                variant='contained' 
                color="primary" 
                onClick={(() => handlePreselectAvailability("evening"))}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                <WbTwilight sx={{ mr: 1, fontSize: 20 }} />
                Evening
              </Button>
              <Button 
                variant='contained' 
                color="primary"
                onClick={(() => handlePreselectAvailability("night"))}
                sx={{ textTransform: 'none', fontWeight: 600, flex: { xs: 1, sm: 'none' } }}
              >
                <Nightlight sx={{ mr: 1, fontSize: 20 }} />
                Night
              </Button>
            </Box>
          </Paper>

          {/* Day Selection Section */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'text.primary' }}>
              Select Day
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {
                Array.from({ length: 7 }, (_, day) => {
                  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  let active = false;
                  let hasAvailability = defaultAvailabilities.filter((da) => da.defaultDay === dayNames[day] && JSON.stringify(da.availability) !== "{}").length > 0;
                  if (selectedDay === dayNames[day]) {
                    active = true
                  }
                  return (
                    <Chip
                      key={day}
                      label={dayNames[day]}
                      sx={{ 
                        bgcolor: active ? 'primary.main' : hasAvailability ? 'success.main' : 'error.main', 
                        color: active ? 'primary.contrastText' : '#fff',
                        cursor: 'pointer',
                        fontWeight: active ? 700 : 500,
                        fontSize: '0.95rem',
                        '&:hover': { 
                          bgcolor: active ? 'primary.dark' : hasAvailability ? 'success.dark' : 'error.dark',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        const targetDay = dayNames[day];
                        const hasChanges = JSON.stringify(newAvailability) !== JSON.stringify(availability);
                        if (hasChanges) {
                          setPendingDay(targetDay);
                          setConfirmOpen(true);
                          return;
                        }
                        setSelectedDay(targetDay)
                      } }
                    />
                  );
                })
              }
            </Box>
          </Paper>

          {/* Availability Defaults Section */}
          <Accordion 
            onChange={(event, isExpanded) => setAccordionExpanded(isExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  {moment(selectedDay, 'ddd').format("dddd")} Schedule
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {mergedAvailability.length > 0 
                      ? `${mergedAvailability[0].split('-')[0]} - ${mergedAvailability[mergedAvailability.length - 1].split('-')[1]}`
                      : 'Unavailable'
                    }
                  </Typography>
                </Box>
                {!accordionExpanded && (
                  <Typography variant="caption" sx={{ color: 'action.disabled', fontSize: '0.75rem', mt: 0.5 }}>
                    Expand for fine control
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
                {/* AM Column (0-11) */}
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: { xs: 1, md: 0 }, overflow: 'hidden' }}>
                  {Array.from({ length: 12 }, (_, hour) => {
                    return (<TimeGridRow key={"default" + hour}
                      hour={hour}
                      availability={newAvailability[hour]}
                      mergedAvailability={mergedAvailability}
                      selectedDay={selectedDay}
                      onClick={(newState, newRange) => {
                        newAvailability[hour] = newState === 'unavailable' ? '' : newRange;
                        setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(newAvailability).filter((v: string) => v !== '')))));
                        setNewAvailability(JSON.parse(JSON.stringify(newAvailability)));
                      }}
                    />
                    )
                  })}
                </Box>
                {/* PM Column (12-23) */}
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: { xs: 1, md: 0 }, overflow: 'hidden', borderLeft: { md: 'none' } }}>
                  {Array.from({ length: 12 }, (_, index) => {
                    const hour = 12 + index;
                    return (<TimeGridRow key={"default" + hour}
                      hour={hour}
                      availability={newAvailability[hour]}
                      mergedAvailability={mergedAvailability}
                      selectedDay={selectedDay}
                      onClick={(newState, newRange) => {
                        newAvailability[hour] = newState === 'unavailable' ? '' : newRange;
                        setMergedAvailability(JSON.parse(JSON.stringify(mergeAvailability(Object.values(newAvailability).filter((v: string) => v !== '')))));
                        setNewAvailability(JSON.parse(JSON.stringify(newAvailability)));
                      }}
                    />
                    )
                  })}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogContent>
              <Typography>
                You have unsaved changes. Switching days will discard them. Continue?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirmOpen(false);
                  if (pendingDay) {
                    setSelectedDay(pendingDay);
                    setPendingDay(null);
                  }
                }}
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </DialogActions>
          </Dialog>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting} sx={{ flex: { xs: 1, sm: 'none' } }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? null : <CheckCircleIcon />}
          sx={{ flex: { xs: 1, sm: 'none' } }}
        >
          {isSubmitting ? 'Submitting...' : 'Save Availability'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefaultAvailabilitySubmissionModal;